'use client';

import Image from 'next/image';
import { useTranslations } from 'next-intl';
import { useEffect, useRef, useState } from 'react';
import { BaseLineWorld } from '@/app/components/icons/BaseLineWorld';
import { ChannelDiscord } from '@/app/components/icons/ChannelDiscord';
import { ChannelWhiteWeChat } from '@/app/components/icons/ChannelWhiteWeChat';
import { ClientLink } from '@/app/components/shared/ClientLink';
import { ImageWithPlaceholder } from '@/app/components/shared/ImageWithPlaceholder';
import { QRCODE_URL, SITE } from '@/app/constants';
import { cn, reportEvent } from '@/app/utils';
import { openOrRedirect } from '@/app/utils/client';
import { useDeferredMediaRender } from '../useDeferredMediaRender';
import { IMG_CONFIG, MB_IMG_CONFIG } from './constants';

const JoinOur = () => {
  const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });
  const containerRef = useRef<HTMLDivElement>(null);
  const { ref: mediaGateRef, shouldRenderMedia } = useDeferredMediaRender<HTMLDivElement>();
  const tJoin = useTranslations('home3.joinOur');

  useEffect(() => {
    if (!shouldRenderMedia) return;

    const container = containerRef.current;
    if (!container) return;

    const handleMouseMove = (e: MouseEvent): void => {
      const { left, top, width, height } = container.getBoundingClientRect();

      // 计算鼠标在容器内的相对位置（范围从-0.5到0.5）
      const x = (e.clientX - left) / width - 0.5;
      const y = (e.clientY - top) / height - 0.5;

      setMousePosition({ x, y });
    };

    // 直接添加mousemove监听，简化方案
    container.addEventListener('mousemove', handleMouseMove);

    // 当鼠标离开容器时，重置位置
    const handleMouseLeave = () => {
      setMousePosition({ x: 0, y: 0 });
    };

    container.addEventListener('mouseleave', handleMouseLeave);

    return () => {
      container.removeEventListener('mousemove', handleMouseMove);
      container.removeEventListener('mouseleave', handleMouseLeave);
    };
  }, [shouldRenderMedia]);

  return (
    <div
      ref={(node) => {
        mediaGateRef.current = node;
      }}
      className="relative h-[74vh]"
    >
      <div className="absolute h-full w-full bg-[linear-gradient(337deg,#006aff_5.33%,#000_63.82%)] opacity-40" />
      <div className="pointer-events-none absolute z-[10] flex h-full w-full flex-col items-center justify-center">
        <div className="flex flex-col items-center justify-center">
          <div className="mb-4 flex h-[32px] w-[64px] items-center justify-center rounded-full bg-system-white24 md:hidden">
            <BaseLineWorld className="size-6 text-system-white" />
          </div>
          <h2 className="font-medium text-[28px] leading-normal md:text-[24px] md:leading-none">{tJoin('title')}</h2>
          <div className="font-semibold text-[64px] leading-none md:text-[48px] md:leading-none">
            {tJoin('subTitle')}
          </div>
          <div className="mt-16 flex gap-8 md:flex-col">
            {SITE === 'china' && (
              <div
                className="group pointer-events-auto relative flex cursor-pointer items-center justify-center gap-1 rounded-full bg-[#50C365] px-6 py-2 md:py-[10px]"
                onClick={() => {
                  reportEvent('element_click', {
                    element_name: 'Community-Join Wechat',
                  });
                }}
              >
                <ChannelWhiteWeChat className="size-6" />
                <div className="font-semibold text-[14px] text-white">{tJoin('joinWechat')}</div>
                <div className="absolute top-full z-10 hidden pt-2 group-hover:block">
                  <div className="h-[248px] w-[216px] rounded-12 border border-system-white12 bg-system-bg02 px-2 py-3">
                    <div className="mx-auto h-[200px] w-[200px]">
                      <Image
                        src={QRCODE_URL}
                        alt="微信群二维码"
                        width={200}
                        height={200}
                        className="rounded-6 bg-[#3C4352]"
                      />
                    </div>
                    <p className="mt-2 text-center font-[400] text-system-white64 text-xs tracking-[0]">
                      加入讨论群，了解最新动态
                    </p>
                  </div>
                </div>
              </div>
            )}
            {SITE === 'global' && (
              <>
                <div
                  className="pointer-events-auto flex cursor-pointer items-center justify-center gap-1 rounded-full bg-[#006aff] px-6 py-2 md:py-[4px]"
                  onClick={() => {
                    reportEvent('element_click', {
                      element_name: 'Community-Join Discord',
                    });
                    openOrRedirect('https://discord.gg/3pDU8fmQ8Y');
                  }}
                >
                  <ChannelDiscord className="m-2 size-6" />
                  <div className="font-semibold text-[14px] text-white">{tJoin('joinDiscord')}</div>
                </div>
                <ClientLink
                  href="/cpp"
                  className="pointer-events-auto flex cursor-pointer items-center justify-center rounded-full border-[2px] border-system-white24 px-8 py-2"
                  onClick={() => {
                    reportEvent('element_click', {
                      element_name: 'Community-Apply for CPP',
                    });
                  }}
                >
                  <div className="font-semibold text-[14px] text-white leading-[28px]">{tJoin('applyForCPP')}</div>
                </ClientLink>
              </>
            )}
          </div>
        </div>
      </div>
      {shouldRenderMedia ? (
        <>
          <div ref={containerRef} className="absolute top-0 right-0 bottom-0 left-0 block overflow-hidden md:hidden">
            {IMG_CONFIG.map((item, index) => {
              const depth = 0.1 + (index % 3) * 0.05;
              return (
                <div
                  key={item.title}
                  className={cn(`absolute transition-transform duration-300 ease-out ${item.position}`)}
                  style={{
                    transform: `translate(${mousePosition.x * -depth * 300}px, ${mousePosition.y * -depth * 300}px)`,
                  }}
                >
                  <ImageWithPlaceholder src={item.image} alt={item.title} className="h-full" />
                </div>
              );
            })}
          </div>
          <div className="absolute top-0 right-0 hidden h-full w-full overflow-hidden md:block">
            <img
              src={MB_IMG_CONFIG}
              alt="MB_IMG_CONFIG"
              width={1502}
              height={1230}
              className="size-full object-cover object-center"
              decoding="async"
              loading="lazy"
            />
          </div>
        </>
      ) : null}
    </div>
  );
};

export default JoinOur;
